// Free times: turning the Realtime Database node `freetimes/{customerid}` into calendar events.
//
// Pure by design: no Firebase, no React. Nothing in this repo WRITES that node - there is no
// functions/ directory here at all - so the producer lives in the functions/enduser repo and
// everything here only reads and reshapes.
//
// WHY THIS FILE EXISTS. The same ~70 lines were duplicated byte-for-byte in
// CourtsContainer.listenFreeTimes and WeekPlannerCourtsContainer.listenFreeTimes, both
// unreachable since the Ctrl+Shift trigger on the weekday label was commented out
// (CourtsContainer.jsx:2073). CustomerTimesCalendarContainer would have been the third copy.
//
// THE NODE'S SHAPE, as the original reader proves it:
//
//   freetimes/{customerid}/
//     {epochSeconds}/            <- top-level key, unix seconds as a string
//       start: <scalar>          <- a SIBLING, not a resource id. Must be filtered out.
//       {resourceId}/
//         category: <string>     <- the producer writes it; the original code filtered on it
//         times: [ { duration: <minutes>, price: <number>, key: <string> }, ... ]
//
// KEY SEMANTICS ARE UNRESOLVED, and the original code treats them BOTH ways at once:
//
//   - the future-only filter reads a key as a true instant, comparing parseInt(key) against
//     moment.utc().add(10,'minutes').format('X') - a real epoch
//   - the render reads it as wall clock to show verbatim: moment.utc(key,'X') formatted to a
//     '...Z' string, then displayed literally by calendars whose default zone is the fixed
//     Etc/GMT-3 and whose accessors subtract 180 minutes
//
// Only one can be right, and nothing has rendered these events since the trigger was removed,
// so the code cannot tell us which. If slots land at the times the shop shows, the keys are
// wall-clock-as-UTC and the future filter is loose by the UTC offset (it keeps slots up to ~3h
// past). If they land 2-3h early, the keys are true instants and the render needs the offset
// added. Either fix is a small change here - which is the point of extracting this first.

import moment from 'moment'

// Slots of 200 minutes or more are dropped. The original filter was `x.duration < 200` with no
// explanation; the producer presumably also emits long merged blocks that were judged noise.
// Kept as-is rather than guessed at.
export const MAX_DURATION = 200

// Only slots starting more than this many minutes from now are kept. Strict, so a slot exactly
// on the boundary is dropped - matching the original `_justNow < parseInt(curr)`.
export const GRACE_MINUTES = 10

// The event `start`/`end` format the original wrote, kept verbatim.
//
// Careful: the trailing `Z` is moment's OFFSET token, not a literal - on a UTC moment it
// formats as '+00:00'. So these strings actually look like '2026-09-01T13:00:00.000+00:00',
// which is what CourtsContainer has always produced. `moment()` parses it identically to a
// 'Z' suffix, so this is faithful rather than fixed; do not "correct" it without checking
// whoever consumes the strings.
const EVENT_TIMEFORMAT = 'YYYY-MM-DDTHH:mm:ss.000Z'

const asEpochSeconds = (value) => {
  const parsed = parseInt(value, 10)
  return Number.isNaN(parsed) ? null : parsed
}

// `t.price + ' €'` in the original, which renders 'undefined €' for a missing price. A price of
// ZERO is legitimate and must survive - do not reach for `||` here.
const priceTitle = (price) => {
  const parsed = typeof price === 'number' ? price : parseFloat(price)
  if (!Number.isFinite(parsed)) return ''
  return parsed + ' €'
}

// Turns a raw snapshot value into react-big-calendar events. The shape is deliberately
// identical to what CourtsContainer built, so the two existing containers can adopt this
// unchanged.
//
// `now` is a parameter so the grace boundary is testable; it defaults to the real clock.
export const buildFreeEvents = (snapshotValue, options = {}) => {
  if (!snapshotValue || typeof snapshotValue !== 'object') return []

  const maxDuration = options.maxDuration === undefined ? MAX_DURATION : options.maxDuration
  const now = options.now === undefined ? moment.utc() : moment.utc(options.now)
  const cutoff = parseInt(now.clone().add(GRACE_MINUTES, 'minutes').format('X'), 10)

  const events = []

  Object.keys(snapshotValue).forEach((key) => {
    const startsAt = asEpochSeconds(key)
    if (startsAt === null) return
    if (!(cutoff < startsAt)) return

    const slot = snapshotValue[key]
    if (!slot || typeof slot !== 'object') return

    Object.keys(slot)
      .filter((k) => k !== 'start')
      .forEach((resourceId) => {
        const resource = slot[resourceId]
        if (!resource || typeof resource !== 'object') return
        const times = Array.isArray(resource.times) ? resource.times : []

        times.forEach((time) => {
          if (!time) return
          // A missing duration fails this comparison and is dropped, as before.
          if (!(time.duration < maxDuration)) return

          const start = moment.utc(String(startsAt), 'X')
          events.push({
            allDay: false,
            start: start.format(EVENT_TIMEFORMAT),
            end: start.clone().add(time.duration, 'minutes').format(EVENT_TIMEFORMAT),
            id: time.key,
            data: { state: 'open', category: resource.category, duration: time.duration },
            paymentstatus: { cost: 0, paid: 0 },
            resourceId,
            title: priceTitle(time.price),
          })
        })
      })
  })

  return events
}

// The day filter the calendars never had. CourtsContainer pushed EVERY future free slot into
// one day's calendar (`events.push(...this.state.freeEvents)`), leaving react-big-calendar to
// walk tens of thousands of objects to lay out a single day - the slowness that made the
// feature unusable alongside reservations.
//
// Events carry UTC-wall-clock strings, so the day is the date part of `start`.
export const freeEventsForDate = (events, isoDate) => {
  if (!Array.isArray(events) || !isoDate) return []
  return events.filter((event) => event && moment.utc(event.start).format('YYYY-MM-DD') === isoDate)
}

// Key bounds for a per-day `orderByKey().startAt().endAt()` query, so the browser stops
// downloading the venue's entire future every time anything under the node changes.
//
// Keys are 10-digit epoch seconds and orderByKey compares them as STRINGS, so this relies on
// lexicographic order matching numeric order - true while every key has the same digit count,
// i.e. until 2286.
//
// The padding exists because of the unresolved key semantics above: if the producer wrote
// wall-clock-as-UTC epochs the day's real bounds are offset by up to 3 hours, so ask for a
// wider window and let freeEventsForDate trim it precisely.
export const dayKeyRange = (isoDate, paddingHours = 4) => {
  const day = moment.utc(isoDate, 'YYYY-MM-DD', true)
  if (!day.isValid()) return null
  const start = day.clone().startOf('day').subtract(paddingHours, 'hours')
  const end = day.clone().endOf('day').add(paddingHours, 'hours')
  return { startKey: start.format('X'), endKey: end.format('X') }
}

// One line of "is there anything here, and how much" for the header. Also the cheapest way to
// see whether the node holds a handful of slots or thousands.
export const freeEventsSummary = (events) => {
  const list = Array.isArray(events) ? events.filter(Boolean) : []
  const courts = new Set(list.map((e) => e.resourceId).filter(Boolean))
  const prices = list
    .map((e) => parseFloat(String(e.title).replace('€', '').replace(',', '.').trim()))
    .filter((p) => Number.isFinite(p))
  return {
    count: list.length,
    courts: courts.size,
    minprice: prices.length ? Math.min(...prices) : null,
    maxprice: prices.length ? Math.max(...prices) : null,
  }
}

export const formatFreeEventsSummary = (summary) => {
  if (!summary || !summary.count) return 'Ei myytäviä aikoja tälle päivälle'
  const parts = [`${summary.count} aikaa`, `${summary.courts} kentällä`]
  if (summary.minprice !== null) {
    parts.push(
      summary.minprice === summary.maxprice
        ? `${summary.minprice} €`
        : `${summary.minprice}–${summary.maxprice} €`
    )
  }
  return parts.join(' · ')
}
